import React from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import type { User } from "@atlas/shared";
import { useAuth } from "../../hooks/useAuth";
import { Button } from "../../components/Button";
import { telemetry } from "../../lib/telemetry";

// ─── One-off "get the app" step after signing up on a phone ─────────────────
// Creator links open in Instagram's browser, so most phone signups happen on
// the website. Signing up there first is the safe order (the partner code is
// attached at account creation; the stores can't carry it), so this page then
// points them at the app. The dashboard route sends a brand-new account here
// once (router.tsx); skippable, never shown on a computer.
//
// "Once" = a per-user localStorage flag, set the moment the page shows, plus
// a 24-hour window on the account's age so an old account on a new phone is
// never sent here.

const SEEN_PREFIX = "atlas-welcome-app-seen:";
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;

const APP_STORE_URL = "https://apps.apple.com/gb/app/atlas-hiking-record/id6788635039";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=co.uk.goatlas.app&hl=en_GB";

type Phone = "ios" | "android";

function detectPhone(): Phone | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/iPhone|iPod/i.test(ua)) return "ios";
  // iPadOS reports itself as a Mac; touch points give it away.
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return null;
}

function hasSeen(userId: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + userId) !== null;
  } catch {
    // Storage blocked: never risk showing it on every visit.
    return true;
  }
}

function markSeen(userId: string): void {
  try {
    localStorage.setItem(SEEN_PREFIX + userId, "1");
  } catch {
    // best-effort
  }
}

/** True for a brand-new account on a phone that hasn't seen the page. Read by the dashboard route. */
export function shouldOfferApp(user: Pick<User, "id" | "createdAt"> | null | undefined): boolean {
  if (!user || detectPhone() == null) return false;
  const age = Date.now() - new Date(user.createdAt).getTime();
  if (!(age >= 0 && age < NEW_ACCOUNT_MS)) return false;
  return !hasSeen(user.id);
}

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-xl)",
  padding: "var(--space-8)",
  boxShadow: "var(--shadow-md), var(--ring-top)",
};

function StoreBadge({ phone }: { phone: Phone }) {
  const track = () => telemetry.track("action", "welcome_app_store_tap");
  if (phone === "ios") {
    return (
      <a href={APP_STORE_URL} onClick={track} style={{ display: "inline-block", lineHeight: 0 }}>
        <img src="/images/store-badges/app-store-badge.svg" alt="Download on the App Store" width={156} height={52} draggable={false} />
      </a>
    );
  }
  return (
    <a href={GOOGLE_PLAY_URL} onClick={track} style={{ display: "inline-block", lineHeight: 0 }}>
      <img src="/images/store-badges/google-play-badge.png" alt="Get it on Google Play" width={180} height={70} draggable={false} />
    </a>
  );
}

export default function WelcomeAppPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Decided on first render so marking the flag below cannot flip it mid-visit.
  const [phone] = React.useState(detectPhone);
  const [eligible] = React.useState(() => shouldOfferApp(user));

  React.useEffect(() => {
    if (!user || !eligible) return;
    markSeen(user.id);
    telemetry.track("action", "welcome_app_shown");
  }, [user, eligible]);

  if (!user || !eligible || !phone) return <Navigate to="/dashboard" replace />;

  const healthName = phone === "ios" ? "Apple Health" : "Health Connect";

  return (
    <div style={cardStyle}>
      <p className="eyebrow" style={{ fontSize: 11, margin: "0 0 6px" }}>Your account is ready</p>
      <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
        Atlas fits in a pocket too
      </h1>
      <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 10, lineHeight: 1.55 }}>
        The app does everything the website does, and it can bring your walks in from {healthName}, which
        a browser can't reach.
      </p>
      <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.55 }}>
        Sign in the same way you just signed up and everything is already there, including any code you
        came in with. No need to enter it again.
      </p>

      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <StoreBadge phone={phone} />
      </div>

      <div style={{ marginTop: 20 }}>
        <Button
          variant="ghost"
          block
          onClick={() => {
            telemetry.track("action", "welcome_app_skipped");
            void navigate({ to: "/dashboard", replace: true });
          }}
        >
          Carry on in the browser
        </Button>
      </div>
    </div>
  );
}
